<template>
  <window
    ref="modal"
    id="dial-pad-modal"
    headless
    centered
    @show="onShow"
    @hide="onHide"
    :class="{
      calling: !!calls.length,
      inbound: (!!calls.length && (status === CALL_STATUSES.HAVING_CALL))
    }"
  >
    <template slot="body">
      <div class="dial-pad" v-if="!calls.length">
        <form @submit="execCall">
          <text-input
            borderless
            :format="formatPhoneNumber"
            :validate="validatePhoneNumber"
            v-model="phoneNumber"
          />

          <button type="submit" cloak />
        </form>

        <b-row class="dial-wrapper" v-if="!calls.length">
          <b-col v-for="(btn, index) in Array(9).fill(0)" :key="index" cols="4" class="btn-wrapper">
            <div class="dial-btn" @click="() => dial(index + 1)">{{ (index + 1) }}</div>
          </b-col>
          <b-col cols="4" offset="4" class="btn-wrapper">
            <div class="dial-btn" @click="() => dial(0)">0</div>
          </b-col>
        </b-row>

        <div
          class="call-btn"
          :class="{ disabled: (!isValidPhoneNumber(phoneNumber) || !connected) }"
          @click="() => call()"
        >
          <font-awesome-icon :icon="faPhone" />
        </div>
      </div>

      <div class="in-call" v-if="!!calls.length" :class="{ inbound: (status === CALL_STATUSES.HAVING_CALL) }">
        <logo class />
        <div
          class="call-info"
          v-for="item in (engagingCall ? [engagingCall] : calls)"
          :key="item.id"
        >
          <h3>{{ name[item.number] || $t('UNKNOWN_CONTACT') }}</h3>
          <p @click="copyToClipboard(item.number)" class="number">
            <span>{{ item.number }}</span>
            <font-awesome-icon :icon="faCopy" title="Copy" class="copy-icon" />
          </p>
          <div><small>{{ $t(status) }}</small></div>
          <div v-if="(status === CALL_STATUSES.TALKING)"><small>{{ duration }}</small></div>

          <div
            class="call-btn answer"
            @click="() => answer(item.id)"
            v-if="(status === CALL_STATUSES.HAVING_CALL)"
          >
            <font-awesome-icon :icon="faPhone" />
          </div>

          <div
            class="call-btn terminate"
            @click="() => terminate(item.id)"
          >
            <font-awesome-icon :icon="faPhoneSlash" />
          </div>
        </div>
      </div>
    </template>
  </window>
</template>

<script>
import { watch, ref, getCurrentInstance } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPhone, faPhoneSlash, faCopy } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import Window from '@/components/Window';
import TextInput from '@/components/TextInput';
import Logo from '@/components/Logo';
import { isValidPhoneNumber } from '@/helpers';
import { CALL_STATUSES } from '@/consts';
import phoneControllerServices from '@/components/PhoneController/PhoneControllerServices';

const { useState: usePhoneState, useActions: usePhoneActions } = createNamespacedHelpers('phone');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');

export default {
  name: 'DialPadModal',
  components: {
    Window,
    TextInput,
    FontAwesomeIcon,
    Logo
  },
  props: {
    engagingCall: {
      type: Object,
      default: () => {}
    }
  },
  setup(props, { refs, emit }) {
    const vm = getCurrentInstance();

    const { dialPadActive: isOpen } = usePhoneState(['dialPadActive']);
    const { toggleDialPad: toggle } = usePhoneActions(['toggleDialPad']);
    const { calls, connected, status } = usePhoneState(['calls', 'connected', 'status']);
    const { info } = useFlashActions(['info']);

    const phoneNumber = ref('');
    const name = ref({});
    const duration = ref('');

    const off = () => {
      refs.modal.hide();
    };

    const onShow = () => {};

    const onHide = () => {
      toggle(false);
    };

    const formatPhoneNumber = (val) => (val.replace(/[^0-9+]/g, ''));

    const copyToClipboard = (val) => {
      const el = document.createElement('textarea');
      el.value = val;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      info('COPY_PHONE_SUCCESS');
    };

    const validatePhoneNumber = (val) => {
      if (!isValidPhoneNumber(val)) {
        return 'INVALID_PHONE_NUMBER';
      }

      return '';
    };

    const fetchClientInfo = (number) => {
      new Promise((resolve) => {
        const loadData = async () => {
          try {
            const client = await phoneControllerServices.getClientByPhoneNumber(number);
            resolve(client);
          } catch(e) {
            resolve(null);
          }
        };

        loadData();
      }).then((client) => {
        if (client) {
          const { firstName, lastName } = client;
          name.value[number] = `${firstName} ${lastName}`;
        } else {
          name.value[number] = vm.$i18n.t('UNKNOWN_CONTACT');
        }
      });
    };

    const dial = (val) => {
      phoneNumber.value += val.toString();
    };

    const call = () => {
      emit('call', phoneNumber.value);
    };

    const execCall = (e) => {
      e.preventDefault();
      call();
    };

    const answer = (id) => {
      emit('answer', id);
    };

    const terminate = (id) => {
      emit('terminate', id);
    };

    let timerInterval;
    const stopTimer = () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
    };

    const startTimer = () => {
      stopTimer();

      timerInterval = setInterval(() => {
        duration.value = moment.utc(moment().diff(props.engagingCall.startTime)).format('mm:ss');
      }, 1000);
    };

    watch(isOpen, (newVal) => {
      const { modal } = refs;

      if (newVal) modal.show();
      else modal.hide();
    });

    watch(calls, (vals) => {
      if (vals && vals.length) {
        vals.forEach((val) => fetchClientInfo(val.number));
      }
    });

    watch(status, (val) => {
      if (val === CALL_STATUSES.TALKING) {
        startTimer();
      } else {
        stopTimer();
      }
    });

    return {
      isOpen,
      off,
      onShow,
      onHide,
      formatPhoneNumber,
      validatePhoneNumber,
      phoneNumber,
      dial,
      faPhone,
      faPhoneSlash,
      isValidPhoneNumber,
      call,
      execCall,
      name,
      calls,
      connected,
      status,
      CALL_STATUSES,
      answer,
      terminate,
      duration,
      faCopy,
      copyToClipboard
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#dial-pad-modal {
  .body {
    padding-top: 50px;
  }

  &.calling {
    .body {
      background-color: $geranium;
      color: $babyPowder;

      .close {
        .line {
          background-color: $babyPowder;
        }
      }
    }

    &.inbound {
      .body {
        background-color: $babyPowder;
        color: $independence;

        .close {
          .line {
            background-color: $babyPowder;
          }
        }
      }
    }
  }

  .dial-pad {
    width: 180px;
    margin: 0 auto;

    .text-input {
      margin-bottom: 20px;

      input {
        border-bottom: 2px solid $paradisePink;
        text-align: right;
        font-weight: bold;
        color: $paradisePink;
      }
    }

    .dial-wrapper {
      margin-left: -7.5px;
      margin-right: -7.5px;
    }

    .btn-wrapper {
      margin-bottom: 15px;
      padding-left: 7.5px;
      padding-right: 7.5px;

      .dial-btn {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: 2px solid $independence;
        text-align: center;
        padding-top: 12px;
        font-weight: bold;
        transition: all .2s ease-in-out;
        cursor: pointer;
        user-select: none;

        &:hover {
          background-color: $paradisePink;
          border-color: $paradisePink;
          color: $babyPowder;
        }
      }
    }

    .call-btn {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: $independence;
      color: $babyPowder;
      cursor: pointer;
      margin: 15px auto;
      font-size: 20px;
      text-align: center;
      padding-top: 9px;
      transition: all .2s ease-in-out;
      box-sizing: border-box;
      border: 2px solid $independence;

      &:hover {
        background-color: $babyPowder;
        color: $independence;
      }

      &.disabled {
        background-color: $lavenderGrey;
        border-color: $lavenderGrey;
        color: $babyPowder;
        cursor: default;
      }
    }
  }

  .in-call {
    text-align: center;
    width: 180px;
    padding-bottom: 10px;
    transition: all .2s ease-in-out;

    .call-info{
      margin-bottom: 30px;
      padding-bottom: 15px;
      border-bottom: 1px solid $alto;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border: 0;
      }

      .number {
        cursor: pointer;

        .copy-icon {
          margin-left: 7px;
        }
      }
    }

    .logo {
      width: 100px;
      margin-bottom: 15px;
    }

    h3 {
      font-size: 18px;
      text-transform: uppercase;
      margin-bottom: 3px;
      font-weight: bold;
    }

    .call-btn {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: $babyPowder;
      color: $geranium;
      cursor: pointer;
      margin: 15px auto;
      font-size: 20px;
      text-align: center;
      padding-top: 9px;
      transition: all .2s ease-in-out;
      box-sizing: border-box;
      display: inline-block;

      &:hover {
        color: $independence;
      }
    }

    &.inbound {
      .logo {
        color: $geranium;
      }

      .call-btn {
        background-color: $geranium;
        color: $white;
        margin: 15px;
        border: 2px solid $geranium;

        &:hover {
          background: $babyPowder;
          color: $geranium;
        }

        &.answer {
        background-color: $shamrockGreen;
        color: $babyPowder;
        border: 2px solid $shamrockGreen;

          &:hover {
            background-color: $babyPowder;
            color: $shamrockGreen;
          }
        }
      }
    }
  }
}
</style>
